import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://marinconstruesfuerzo.com';

const legalJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/aviso-legal`,
  url: `${siteUrl}/aviso-legal`,
  name: 'Aviso Legal | Marin Construesfuerzo SL',
  description: 'Aviso legal de Marin Construesfuerzo SL. Datos identificativos, condiciones de uso, propiedad intelectual y legislación aplicable.',
  inLanguage: 'es-ES',
  isPartOf: { '@id': `${siteUrl}/#website` },
  publisher: {
    '@type': 'Organization',
    name: 'Marin Construesfuerzo SL',
    url: siteUrl,
  },
};

export default function LegalNotice() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Aviso Legal | Marin Construesfuerzo SL"
        description="Aviso legal de Marin Construesfuerzo SL — Datos identificativos, condiciones de uso, propiedad intelectual y legislación aplicable. Empresa de construcción en Arrecife, Lanzarote."
        canonical={`${siteUrl}/aviso-legal`}
        jsonLd={legalJsonLd}
      />
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Aviso <strong className="text-amber-500">Legal</strong>
          </h1>
          
          <p className="text-gray-600 mb-8">
            Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Datos Identificativos</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="mb-2">
                  En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y Comercio Electrónico (LSSI-CE), se informa a los usuarios de los datos identificativos del titular de este sitio web:
                </p>
                <ul className="space-y-2 mt-4">
                  <li><strong>Denominación social:</strong> Marin Construesfuerzo SL</li>
                  <li><strong>NIF:</strong> B26637975</li>
                  <li><strong>Domicilio social:</strong> CL Victor Hugo, 91, 35500 Arrecife, Las Palmas, Lanzarote, España</li>
                  <li><strong>Teléfono:</strong> <a href="tel:+34613725305" className="text-amber-500 hover:underline cursor-pointer">613 72 53 05</a></li>
                  <li><strong>Email:</strong> <a href="mailto:m.construesfuerzo@gmail.com" className="text-amber-500 hover:underline cursor-pointer">m.construesfuerzo@gmail.com</a></li>
                  <li><strong>Actividad:</strong> Servicios de construcción, reformas y rehabilitación en Lanzarote</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Objeto</h2>
              <p className="mb-4">
                El presente aviso legal regula el uso y utilización del sitio web de <strong>Marin Construesfuerzo SL</strong>, del que es titular la empresa anteriormente mencionada.
              </p>
              <p className="mb-4">
                La navegación por el sitio web atribuye la condición de usuario del mismo e implica la aceptación plena y sin reservas de todas y cada una de las disposiciones incluidas en este Aviso Legal, que pueden sufrir modificaciones.
              </p>
              <p>
                El usuario se obliga a hacer un uso correcto del sitio web de conformidad con las leyes, la buena fe, el orden público, los usos del tráfico y el presente Aviso Legal. El usuario responderá frente a <strong>Marin Construesfuerzo SL</strong> o frente a terceros, de cualesquiera daños y perjuicios que pudieran causarse como consecuencia del incumplimiento de dicha obligación.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Condiciones de Uso</h2>
              <p className="mb-4">
                El acceso y uso de este sitio web confiere la condición de usuario y supone la aceptación de todas las condiciones incluidas en este Aviso Legal. El usuario se compromete a:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Utilizar el sitio web de forma diligente, correcta y lícita</li>
                <li>No utilizar el sitio web con fines o efectos ilícitos, prohibidos en este Aviso Legal, lesivos de los derechos e intereses de terceros</li>
                <li>No realizar actividades publicitarias o de explotación comercial remitiendo mensajes que utilicen una identidad falsa</li>
                <li>No transmitir, difundir o poner a disposición de terceros cualquier tipo de información, elemento o contenido que constituya publicidad ilícita o desleal</li>
                <li>No introducir virus informáticos, archivos defectuosos, o cualquier otro programa que pueda causar daños en los sistemas informáticos</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Propiedad Intelectual e Industrial</h2>
              <p className="mb-4">
                Todos los contenidos del sitio web, incluyendo sin carácter limitativo, textos, fotografías, gráficos, imágenes, iconos, tecnología, software, links y demás contenidos audiovisuales o sonoros, así como su diseño gráfico y códigos fuente, son propiedad intelectual de <strong>Marin Construesfuerzo SL</strong> o de terceros, sin que puedan entenderse cedidos al usuario ninguno de los derechos de explotación reconocidos por la normativa vigente en materia de propiedad intelectual sobre los mismos.
              </p>
              <p className="mb-4">
                Las marcas, nombres comerciales o signos distintivos son titularidad de <strong>Marin Construesfuerzo SL</strong> o de terceros, sin que pueda entenderse que el acceso al sitio web atribuya ningún derecho sobre las citadas marcas, nombres comerciales y/o signos distintivos.
              </p>
              <p>
                Queda prohibida la reproducción, distribución, comunicación pública, transformación o cualquier otra actividad que se pueda realizar con los contenidos del sitio web sin la autorización previa y por escrito de <strong>Marin Construesfuerzo SL</strong>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Exclusión de Garantías y Responsabilidad</h2>
              <p className="mb-4">
                <strong>Marin Construesfuerzo SL</strong> no se hace responsable, en ningún caso, de los daños y perjuicios de cualquier naturaleza que pudieran ocasionar, a título enunciativo:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Errores u omisiones en los contenidos</li>
                <li>Falta de disponibilidad del sitio web o la transmisión de virus o programas maliciosos o lesivos en los contenidos</li>
                <li>Uso ilícito, negligente, fraudulento o contrario al presente Aviso Legal</li>
                <li>Falta de licitud, calidad, fiabilidad, utilidad y disponibilidad de los servicios prestados por terceros y puestos a disposición de los usuarios en el sitio web</li>
                <li>Daños causados por fallos o desconexiones en las redes de telecomunicaciones que produzcan la suspensión, cancelación o interrupción del servicio del sitio web</li>
              </ul>
              <p className="mt-4">
                <strong>Marin Construesfuerzo SL</strong> se reserva el derecho de suspender temporalmente, y sin previo aviso, la accesibilidad al sitio web con motivo de operaciones de mantenimiento, reparación, actualización o mejora.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Enlaces Externos</h2>
              <p className="mb-4">
                El sitio web puede contener enlaces a otros sitios web de terceros. <strong>Marin Construesfuerzo SL</strong> no asume ninguna responsabilidad por el contenido, información o servicios que pudieran aparecer en dichos sitios, que tendrán exclusivamente carácter informativo y que en ningún caso implican relación alguna entre <strong>Marin Construesfuerzo SL</strong> y las personas o entidades titulares de tales contenidos o titulares de los sitios donde se encuentren.
              </p>
              <p>
                Si un usuario tuviera conocimiento de la ilicitud de actividades desarrolladas a través de estos sitios web ajenos, deberá comunicarlo inmediatamente a <strong>Marin Construesfuerzo SL</strong> a efectos de que se proceda a deshabilitar el enlace de acceso a los mismos.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Protección de Datos Personales</h2>
              <p className="mb-4">
                <strong>Marin Construesfuerzo SL</strong> se compromete a cumplir con la legislación vigente en materia de protección de datos de carácter personal y con las obligaciones que de ella se derivan.
              </p>
              <p className="mb-4">
                Para más información sobre el tratamiento de datos personales, consulte nuestra <a href="/politica-de-privacidad" className="text-amber-500 hover:underline cursor-pointer">Política de Privacidad</a>.
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
                <p className="text-sm">
                  <strong>Responsable del tratamiento:</strong> Marin Construesfuerzo SL<br />
                  <strong>Finalidad:</strong> Gestión de consultas y prestación de servicios de <strong>construcción en Lanzarote</strong><br />
                  <strong>Legitimación:</strong> Consentimiento del interesado<br />
                  <strong>Destinatarios:</strong> No se cederán datos a terceros, salvo obligación legal<br />
                  <strong>Derechos:</strong> Acceso, rectificación, supresión, limitación, portabilidad y oposición
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Política de Cookies</h2>
              <p>
                Este sitio web utiliza cookies para mejorar la experiencia del usuario. Para más información sobre el uso de cookies, consulte nuestra <a href="/politica-de-cookies" className="text-amber-500 hover:underline cursor-pointer">Política de Cookies</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Duración y Modificación</h2>
              <p className="mb-4">
                <strong>Marin Construesfuerzo SL</strong> se reserva el derecho de efectuar sin previo aviso las modificaciones que considere oportunas en su sitio web, pudiendo cambiar, suprimir o añadir tanto los contenidos y servicios que se presten a través de la misma como la forma en la que éstos aparezcan presentados o localizados.
              </p>
              <p>
                La vigencia de las citadas condiciones irá en función de su exposición y estarán vigentes hasta que sean modificadas por otras debidamente publicadas.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Legislación Aplicable y Jurisdicción</h2>
              <p className="mb-4">
                Las presentes condiciones generales se rigen por la legislación española vigente.
              </p>
              <p className="mb-4">
                Para la resolución de cualquier controversia o conflicto que pueda surgir con motivo de la validez, interpretación o cumplimiento del presente Aviso Legal, <strong>Marin Construesfuerzo SL</strong> y el usuario acuerdan someterse expresamente a los Juzgados y Tribunales de <strong>Las Palmas de Gran Canaria</strong>, renunciando expresamente a cualquier otro fuero que pudiera corresponderles.
              </p>
              <p>
                En el caso de que cualquier disposición o disposiciones de este Aviso Legal fueren consideradas nulas o inaplicables, en su totalidad o en parte, por cualquier Juzgado, Tribunal u órgano administrativo competente, dicha nulidad o inaplicación no afectará a las otras disposiciones del Aviso Legal.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Información sobre Servicios</h2>
              <p className="mb-4">
                <strong>Marin Construesfuerzo SL</strong> ofrece servicios profesionales de:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Construcción de obra nueva</strong> en Lanzarote</li>
                <li><strong>Reformas integrales</strong> de viviendas y locales comerciales</li>
                <li><strong>Rehabilitación</strong> de edificios y estructuras</li>
                <li><strong>Albañilería</strong> y trabajos de mampostería</li>
                <li><strong>Pintura</strong> interior y exterior</li>
                <li><strong>Fontanería</strong> y sistemas de climatización</li>
                <li><strong>Electricidad</strong> y domótica</li>
              </ul>
              <p className="mt-4">
                Todos nuestros servicios se prestan en <strong>Arrecife y toda la isla de Lanzarote</strong>, cumpliendo con la normativa técnica y de seguridad vigente.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contacto</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="mb-4">
                  Para cualquier consulta, sugerencia o reclamación relacionada con este Aviso Legal o con los servicios de <strong>construcción en Lanzarote</strong> que ofrecemos, puede contactar con nosotros a través de:
                </p>
                <p className="mb-1"><strong>Marin Construesfuerzo SL</strong></p>
                <p className="mb-1">NIF: B26637975</p>
                <p className="mb-1">Dirección: CL Victor Hugo, 91, 35500 Arrecife, Las Palmas, Lanzarote, España</p>
                <p className="mb-1">Teléfono: <a href="tel:+34613725305" className="text-amber-500 hover:underline cursor-pointer">613 72 53 05</a></p>
                <p className="mb-1">Email: <a href="mailto:m.construesfuerzo@gmail.com" className="text-amber-500 hover:underline cursor-pointer">m.construesfuerzo@gmail.com</a></p>
                <p className="mt-4 text-sm text-gray-600">
                  Horario de atención: Lunes a Viernes de 8:00 a 18:00 horas
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
